import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../components/shared/toast';
import ConfirmationModal from '../../components/shared/ConfirmationModal';
import { getImageUrl } from '../../utils/imageUrl';
import en from '../../translations/en.json';
import './Profile.css';

function Profile() {
  const { user, updateProfile, changePassword } = useAuth();
  const t = (key) => key.split('.').reduce((obj, i) => (obj ? obj[i] : null), en) || key;
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'password', 'permissions'

  // Update Profile Form State
  const [firstname, setFirstname] = useState(user?.firstname || '');
  const [lastname, setLastname] = useState(user?.lastname || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(user?.profile_image || '');
  const [isUpdating, setIsUpdating] = useState(false);
  const fileInputRef = useRef(null);

  // Change Password Form State & Password Toggles
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPass, setIsChangingPass] = useState(false);

  // Confirmation Modal State
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: 'warning',
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    onConfirm: null
  });

  const defaultAvatar = "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150&h=150";

  useEffect(() => {
    if (user) {
      setFirstname(user.firstname || '');
      setLastname(user.lastname || '');
      setEmail(user.email || '');
      if (!profileImageFile) {
        setProfileImagePreview(user.profile_image || '');
      }
    }
  }, [user]);

  // Handle Image Selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('profile.validationImageType'));
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(t('profile.validationImageSize'));
        return;
      }
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  // Perform Update Profile API Call
  const performUpdateProfile = async () => {
    setIsUpdating(true);
    setConfirmModal({ ...confirmModal, isOpen: false });

    try {
      let payload;
      if (profileImageFile) {
        payload = new FormData();
        payload.append('firstname', firstname.trim());
        payload.append('lastname', lastname.trim());
        payload.append('email', email.trim());
        payload.append('profile_image', profileImageFile);
      } else {
        payload = {
          firstname: firstname.trim(),
          lastname: lastname.trim(),
          email: email.trim(),
          profile_image: profileImagePreview || null
        };
      }

      await updateProfile(payload);
      toast.success(t('profile.profileUpdatedSuccess'));
      setProfileImageFile(null);
    } catch (err) {
      toast.error(err.message || t('profile.profileUpdateError'));
    } finally {
      setIsUpdating(false);
    }
  };

  // Trigger Confirmation Modal for Profile Update
  const handleUpdateProfileClick = (e) => {
    e.preventDefault();
    if (!firstname.trim() || !lastname.trim() || !email.trim()) {
      toast.error(t('profile.validationProfileRequired'));
      return;
    }

    setConfirmModal({
      isOpen: true,
      type: 'info',
      title: t('profile.profileUpdateConfirmTitle'),
      message: t('profile.profileUpdateConfirmMessage'),
      confirmLabel: t('profile.profileUpdateConfirmLabel'),
      cancelLabel: 'Cancel',
      onConfirm: performUpdateProfile
    });
  };

  // Perform Change Password API Call
  const performChangePassword = async () => {
    setIsChangingPass(true);
    setConfirmModal({ ...confirmModal, isOpen: false });

    try {
      const res = await changePassword({
        currentPassword,
        newPassword,
        confirmPassword
      });
      toast.success(res.message || t('profile.passwordChangedSuccess'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      toast.error(err.message || t('profile.passwordChangeError'));
    } finally {
      setIsChangingPass(false);
    }
  };

  const validatePasswordSecurity = (pass) => {
    if (!pass || pass.trim().length < 8) {
      return t('profile.passwordSecurityLength');
    }
    if (!/[A-Z]/.test(pass)) {
      return t('profile.passwordSecurityUppercase');
    }
    if (!/[a-z]/.test(pass)) {
      return t('profile.passwordSecurityLowercase');
    }
    if (!/[0-9]/.test(pass)) {
      return t('profile.passwordSecurityNumber');
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pass)) {
      return t('profile.passwordSecuritySpecial');
    }
    return null;
  };

  // Trigger Confirmation Modal for Password Update
  const handleChangePasswordClick = (e) => {
    e.preventDefault();
    if (!currentPassword) {
      toast.error(t('profile.passwordCurrentRequired'));
      return;
    }

    const securityError = validatePasswordSecurity(newPassword);
    if (securityError) {
      toast.error(securityError);
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('profile.passwordMismatch'));
      return;
    }

    setConfirmModal({
      isOpen: true,
      type: 'warning',
      title: t('profile.passwordChangeConfirmTitle'),
      message: t('profile.passwordChangeConfirmMessage'),
      confirmLabel: t('profile.passwordChangeConfirmLabel'),
      cancelLabel: 'Cancel',
      onConfirm: performChangePassword
    });
  };

  const renderEyeIcon = (isVisible) => {
    if (isVisible) {
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
          <line x1="1" y1="1" x2="23" y2="23" />
        </svg>
      );
    }
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    );
  };

  return (
    <div className="profile-page-container">
      {/* Header Banner */}
      <div className="profile-header-card">
        <img
          src={getImageUrl(profileImagePreview) || getImageUrl(user?.profile_image) || defaultAvatar}
          alt={`${user?.firstname} ${user?.lastname}`}
          className="profile-avatar-large"
          onError={(e) => { e.target.src = defaultAvatar; }}
        />
        <div className="profile-header-info">
          <h1 className="profile-name">
            {user?.firstname} {user?.lastname}
            <span className="role-badge">{user?.role_name || 'User'}</span>
          </h1>
          <p className="profile-email">{user?.email} • Username: {user?.username}</p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="profile-tabs-nav">
        <button
          className={`profile-tab-btn ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          {t('profile.tabs.editProfile')}
        </button>
        <button
          className={`profile-tab-btn ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          {t('profile.tabs.changePassword')}
        </button>
        <button
          className={`profile-tab-btn ${activeTab === 'permissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('permissions')}
        >
          {t('profile.tabs.permissions')} ({user?.permissions?.length || 0})
        </button>
      </div>

      {/* Tab Content */}
      <div className="profile-content-card">
        {activeTab === 'profile' && (
          <form onSubmit={handleUpdateProfileClick}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#0f172a' }}>{t('profile.personalDetails')}</h3>

            {/* Profile Image Upload Box */}
            <div className="avatar-upload-container">
              <div className="avatar-preview-box">
                <img
                  src={getImageUrl(profileImagePreview) || defaultAvatar}
                  alt="Profile Avatar Preview"
                  className="avatar-preview-img"
                  onError={(e) => { e.target.src = defaultAvatar; }}
                />
              </div>

              <div className="avatar-upload-actions">
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="btn-upload-file"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                  <span>{t('profile.uploadPhoto')}</span>
                </button>
                <span className="upload-hint-text">
                  {profileImageFile ? `${t('profile.selected')} ${profileImageFile.name}` : t('profile.uploadHint')}
                </span>
              </div>
            </div>

            <div className="profile-form-grid">
              <div className="form-group">
                <label className="form-label">{t('profile.firstName')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('profile.lastName')}</label>
                <input
                  type="text"
                  className="form-input"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('profile.emailAddress')}</label>
                <input
                  type="email"
                  className="form-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
            </div>

            <div style={{ marginTop: '28px', display: 'flex', gap: '12px' }}>
              <button type="submit" className="btn-primary" disabled={isUpdating}>
                {isUpdating ? t('profile.savingProfile') : t('profile.saveProfile')}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'password' && (
          <form onSubmit={handleChangePasswordClick} style={{ maxWidth: '480px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '20px', color: '#0f172a' }}>{t('profile.changePasswordTitle')}</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div className="form-group">
                <label className="form-label">{t('profile.currentPassword')}</label>
                <div className="input-wrapper">
                  <input
                    type={showCurrentPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder={t('profile.currentPasswordPlaceholder')}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    tabIndex={-1}
                    aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                  >
                    {renderEyeIcon(showCurrentPassword)}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('profile.newPassword')}</label>
                <div className="input-wrapper">
                  <input
                    type={showNewPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder={t('profile.newPasswordPlaceholder')}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {renderEyeIcon(showNewPassword)}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('profile.confirmNewPassword')}</label>
                <div className="input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder={t('profile.confirmNewPasswordPlaceholder')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {renderEyeIcon(showConfirmPassword)}
                  </button>
                </div>
              </div>
            </div>

            <div style={{ marginTop: '28px' }}>
              <button type="submit" className="btn-primary" disabled={isChangingPass}>
                {isChangingPass ? t('profile.updatingPassword') : t('profile.updatePassword')}
              </button>
            </div>
          </form>
        )}

        {activeTab === 'permissions' && (
          <div>
            <h3 style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px', color: '#0f172a' }}>{t('profile.permissionsTitle')}</h3>
            <p style={{ fontSize: '14px', color: '#64748b' }}>
              {t('profile.roleLabel')} <strong>{user?.role_name}</strong>
            </p>

            <div className="permissions-grid">
              {user?.role_name === 'Super Admin' ? (
                <div style={{ background: '#ecfdf5', color: '#047857', padding: '14px', borderRadius: '10px', width: '100%', fontSize: '14px', fontWeight: '600' }}>
                  {t('profile.superAdminMessage')}
                </div>
              ) : user?.permissions && user.permissions.length > 0 ? (
                user.permissions.map((p) => (
                  <span key={p.id} className="permission-chip">
                    🔒 {p.permission_name || `${p.tab_name} - ${p.action}`}
                  </span>
                ))
              ) : (
                <p style={{ color: '#94a3b8', fontSize: '14px', marginTop: '12px' }}>{t('profile.noPermissions')}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Global Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel={confirmModal.confirmLabel}
        cancelLabel={confirmModal.cancelLabel}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
}

export default Profile;
